import type {DefaultOptions} from '../types';
import type {ValueEditorOptions} from '../kp-value-editor/types';
import type {ValueEditorAliasesService} from '../aliases/value-editor-aliases.service';
import type {ValueEditorTypeOrAlias} from '../types';

export const DEFAULT_ALIAS = 'DEFAULT';

export class ValueEditorConfigurationManager<CONFIGURATION = ValueEditorOptions> {

    private currentConfiguration: Map<ValueEditorTypeOrAlias, DefaultOptions<CONFIGURATION>> = new Map();

    constructor(private valueEditorAliasesService: ValueEditorAliasesService, private readonly defaultConfiguration: DefaultOptions<CONFIGURATION>) {
        this.currentConfiguration.set(DEFAULT_ALIAS, defaultConfiguration);
    }

    public forAlias(alias: ValueEditorTypeOrAlias | typeof DEFAULT_ALIAS): ConfigurationBuilder<CONFIGURATION> {
        let fallbackedAlias = alias;
        if (!this.valueEditorAliasesService.isAlias(alias)) {
            fallbackedAlias = DEFAULT_ALIAS;
        }

        return {
            setConfiguration: this.setAliasedConfiguration.bind(this, fallbackedAlias),
            getConfiguration: this.getAliasedConfiguration.bind(this, fallbackedAlias)
        };
    }


    public getDefaults(): DefaultOptions<CONFIGURATION> {
        return {...this.defaultConfiguration};
    }

    public getConfiguration(): DefaultOptions<CONFIGURATION> {
        return this.getAliasedConfiguration(DEFAULT_ALIAS);
    }

    public setConfiguration(configuration: Partial<CONFIGURATION>): DefaultOptions<CONFIGURATION> {
        return this.setAliasedConfiguration(DEFAULT_ALIAS, configuration);
    }


    public setAliasedConfiguration(alias: ValueEditorTypeOrAlias, configuration: Partial<CONFIGURATION>): DefaultOptions<CONFIGURATION> {
        this.currentConfiguration.set(alias, {...this.currentConfiguration.get(alias), ...configuration});

        return this.currentConfiguration.get(alias);
    }

    public getAliasedConfiguration(alias: ValueEditorTypeOrAlias): DefaultOptions<CONFIGURATION> {
        return {...this.getDefaults(), ...this.currentConfiguration.get(alias)};
    }
}

/**
 * @ngdoc type
 * @name ConfigurationBuilder
 * @module portaro.value-editors.configuration
 *
 * @template CONFIGURATION
 *
 * @description
 * Methods for setting and getting configuration.
 */
export interface ConfigurationBuilder<CONFIGURATION> {
    /**
     * @ngdoc method
     * @name ConfigurationBuilder#getConfiguration
     * @module portaro.value-editors.configuration
     *
     * @returns {DefaultOptions<CONFIGURATION>} Current value editor options.
     *
     * @description
     * Returns current value editor options.
     *
     * {@link  AbstractValueEditorConfigurationProvider#getConfiguration}
     */
    getConfiguration(): DefaultOptions<CONFIGURATION>;

    /**
     * @ngdoc method
     * @name ConfigurationBuilder#setConfiguration
     * @module portaro.value-editors.configuration
     *
     * @param {Partial<CONFIGURATION>} configuration New value editor configuration.
     *
     * @returns {DefaultOptions<CONFIGURATION>} Complete new value editor configuration.
     *
     * @description
     * Sets new value editor configuration.
     *
     * {@link  AbstractValueEditorConfigurationProvider#setConfiguration}
     */
    setConfiguration(configuration: Partial<CONFIGURATION>): DefaultOptions<CONFIGURATION>;
}
